import { useId, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Record_ } from './types'
import { TONE_HEX, TONE_DEEP_HEX, tint } from '@/lib/phaseTone'
import { DISPLAY_STAGES as STAGES, displayStageOf, stageStateLine, stageWithDetail } from './stages'
import { useShowMoney } from './moneyVisibility'

// Stage dots take their phase pastel (and the deep ink that sits on it) from
// lib/phaseTone — STAGES already carries each stage's phase tone. The steps
// are the 7 glossary stages (Approved sits in Quoted, Completed in In progress).

// ─── Props ────────────────────────────────────────────────────────────────────

interface JourneyStepperProps {
  record: Record_
  /** Optional: allow tapping a stage to navigate. No-op if not provided. */
  onStageClick?: (stageId: string) => void
  /** Phone: start folded to one line, "Step 4 of 7 · Scheduled" (UX-025). */
  collapsible?: boolean
}

/** "Step 4 of 7 · Scheduled", "Step 3 of 7 · Approved", "Step 5 of 7 · Completed". */
export function journeySummary(record: Pick<Record_, 'stage'>): string {
  const idx = STAGES.findIndex((s) => s.id === displayStageOf(record.stage).id)
  return `Step ${idx + 1} of ${STAGES.length} · ${stageWithDetail(record.stage)}`
}

// ─── JourneyStepper ───────────────────────────────────────────────────────────

export function JourneyStepper({ record, onStageClick, collapsible = false }: JourneyStepperProps) {
  const currentIdx = STAGES.findIndex((s) => s.id === displayStageOf(record.stage).id)
  const [open, setOpen] = useState(!collapsible)
  const listId = useId()
  const showMoney = useShowMoney()

  return (
    <div>
      {collapsible ? (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44,
            padding: 0, background: 'none', border: 0, cursor: 'pointer', textAlign: 'left',
            fontSize: 14, fontWeight: 500, color: 'var(--midnight-text2)',
            marginBottom: open ? 10 : 0,
          }}
        >
          <span style={{ flex: 1 }}>{journeySummary(record)}</span>
          <ChevronDown
            aria-hidden
            className={cn('w-4 h-4 transition-transform motion-reduce:transition-none', !open && '-rotate-90')}
            style={{ color: 'var(--midnight-text3)' }}
          />
        </button>
      ) : (
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--midnight-text3)',
            marginBottom: 14,
          }}
        >
          Journey
        </p>
      )}

      {open && <ol id={listId} aria-label="Journey" style={{ position: 'relative', margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Vertical track hairline */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 13,
            top: 6,
            bottom: 6,
            width: 1,
            background: 'var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
            pointerEvents: 'none',
          }}
        />

        {STAGES.map((s, idx) => {
          const Icon = s.icon
          const isPast    = idx < currentIdx
          const isCurrent = idx === currentIdx
          const pastel    = TONE_HEX[s.tone]
          const deep      = TONE_DEEP_HEX[s.tone]

          // Progress fill on the track: filled portion up to current dot
          const showFill = idx < currentIdx

          // Dot style per state
          let dotBg: string
          let dotBorder: string
          let dotColor: string
          let dotScale = 1

          if (isPast) {
            dotBg     = tint(pastel, 20)
            dotBorder = `0.5px solid ${tint(pastel, 45)}`
            dotColor  = pastel
            dotScale  = 1
          } else if (isCurrent) {
            dotBg     = pastel
            dotBorder = `2px solid ${pastel}`
            dotColor  = deep
            dotScale  = 1.1
          } else {
            dotBg     = 'transparent'
            dotBorder = '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))'
            dotColor  = 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))'
            dotScale  = 1
          }

          const isClickable = typeof onStageClick === 'function'

          return (
            <li
              key={s.id}
              aria-current={isCurrent && !isClickable ? 'step' : undefined}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                paddingLeft: 0,
                paddingBottom: idx < STAGES.length - 1 ? 10 : 0,
              }}
            >
              {/* Progress fill segment above this dot (connects dot above to here) */}
              {showFill && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 13,
                    top: -10,
                    width: 1,
                    height: 10,
                    background: tint(pastel, 50),
                    pointerEvents: 'none',
                  }}
                />
              )}

              <RowWrap clickable={isClickable} onClick={() => onStageClick?.(s.id)} current={isCurrent}>
              {/* Stage dot / circle */}
              <span
                aria-hidden="true"
                style={{
                  position: 'relative',
                  zIndex: 1,
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: 9999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: dotBg,
                  border: dotBorder,
                  color: dotColor,
                  transform: `scale(${dotScale})`,
                  transition: 'transform 0.4s cubic-bezier(0.2,0.8,0.2,1), background 0.4s ease, border-color 0.4s ease',
                  // ring color for current
                  ...(isCurrent ? { boxShadow: `0 0 0 3px ${tint(pastel, 25)}` } : {}),
                }}
              >
                {isPast ? (
                  <Check style={{ width: 13, height: 13 }} />
                ) : (
                  <Icon style={{ width: 11, height: 11 }} />
                )}
              </span>

              {/* Stage label + state line */}
              <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: isCurrent ? 600 : 400,
                    letterSpacing: '-0.010em',
                    lineHeight: 1.25,
                    // Done steps read as done (check + secondary text), not
                    // struck through — a strike reads as "cancelled" (UX-114).
                    color: isCurrent ? 'rgb(var(--fg-rgb))' : isPast ? 'var(--midnight-text2)' : 'var(--midnight-text3)',
                    transition: 'color 0.4s ease',
                  }}
                >
                  {isCurrent ? stageWithDetail(record.stage) : s.label}
                  {isPast && <span className="sr-only"> (done)</span>}
                  {isCurrent && <span className="sr-only"> (current)</span>}
                </p>
                {isCurrent && (
                  <p
                    style={{
                      fontSize: 12,
                      color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {stageStateLine(record, { money: showMoney })}
                  </p>
                )}
              </div>
              </RowWrap>
            </li>
          )
        })}
      </ol>}
    </div>
  )
}

/** Clickable rows are real ≥44px buttons; static rows are plain layout. */
function RowWrap({ clickable, onClick, current, children }: { clickable: boolean; onClick: () => void; current: boolean; children: React.ReactNode }) {
  const style: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }
  if (!clickable) return <div style={style}>{children}</div>
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? 'step' : undefined}
      style={{ ...style, minHeight: 44, padding: 0, background: 'none', border: 0, textAlign: 'left', cursor: 'pointer' }}
    >
      {children}
    </button>
  )
}

